import { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { alarmDataUri } from './useTaskReminders';

// ---------------------------------------------------------------------------
// usePlannerReminders — in-app (client-side) reminders for Planner EVENTS and
// MEETINGS, mirroring the Tasks reminder system.
//
// Polls GET /planner/reminders every 30s. When an event/meeting reminder comes
// due while the portal is open it surfaces in a due queue, plays a chime and
// fires an OS notification (same ``tag`` the Web Push scheduler uses, so the
// two channels collapse instead of double-alerting). This is the reliable
// channel while the app is open; Web Push remains the closed-app channel.
//
// The first poll after mount only PRIMES the alerted set — it never chimes for
// reminders that were already due when you arrived, so opening the app is
// silent. Only reminders that come due *while open* chime.
// ---------------------------------------------------------------------------
export const planRemKey = (n) => `${n.kind}:${n.id}:${n.reminder_at}`;

export function usePlannerReminders() {
  const [dueQueue, setDueQueue] = useState([]);
  const soundedRef = useRef(new Set());
  const ackRef = useRef(null);
  const audioCtxRef = useRef(null);
  const alarmRef = useRef(null);
  const alarmUnlockedRef = useRef(false);
  const primedRef = useRef(false);

  const getAck = () => {
    if (!ackRef.current) {
      try { ackRef.current = new Set(JSON.parse(localStorage.getItem('planner:remAck') || '[]')); }
      catch { ackRef.current = new Set(); }
    }
    return ackRef.current;
  };
  const persistAck = () => { try { localStorage.setItem('planner:remAck', JSON.stringify([...getAck()])); } catch { /* noop */ } };

  const playChime = useCallback(() => {
    try {
      const a = alarmRef.current;
      if (a) { a.muted = false; a.volume = 1; a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); }
    } catch { /* noop */ }
    try { if (navigator.vibrate) navigator.vibrate([250, 120, 250, 120, 400]); } catch { /* noop */ }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      let ctx = audioCtxRef.current;
      if (!ctx) { ctx = new Ctor(); audioCtxRef.current = ctx; }
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const base = ctx.currentTime + 0.02;
      [880, 1174.66, 1567.98, 1174.66, 1567.98].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle'; o.frequency.value = freq;
        const t0 = base + i * 0.2;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
        o.connect(g); g.connect(ctx.destination);
        o.start(t0); o.stop(t0 + 0.65);
      });
    } catch { /* audio unsupported */ }
  }, []);

  const acknowledge = useCallback((n) => {
    const k = planRemKey(n);
    getAck().add(k); persistAck();
    setDueQueue((q) => q.filter((x) => planRemKey(x) !== k));
  }, []);

  // Poll for due reminders.
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { data } = await notesApi.get('/planner/reminders');
        if (stop) return;
        const now = Date.now();
        const ack = getAck();
        const due = (data || []).filter((n) => {
          if (!n.reminder_at || new Date(n.reminder_at).getTime() > now) return false;
          if (ack.has(planRemKey(n))) return false;
          return true;
        });
        setDueQueue(due);
        const fresh = due.filter((n) => !soundedRef.current.has(planRemKey(n)));
        const wasPrimed = primedRef.current;
        primedRef.current = true;
        if (fresh.length) {
          fresh.forEach((n) => soundedRef.current.add(planRemKey(n)));
          if (wasPrimed) {
            playChime();
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                fresh.forEach((n) => {
                  const label = n.kind === 'meeting' ? 'Meeting reminder' : 'Event reminder';
                  // Same tag as the Web Push scheduler → the two channels collapse.
                  new Notification(label, {
                    body: n.title || label,
                    tag: `planner-${n.kind}-reminder-${n.id}`,
                    requireInteraction: true,
                  });
                });
              }
            } catch { /* unsupported */ }
          }
        }
      } catch { /* silent */ }
    };
    check();
    const iv = setInterval(check, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stop = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, [playChime]);

  // Unlock audio + request OS-notification permission on the first user gesture.
  useEffect(() => {
    const unlock = () => {
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          Notification.requestPermission().catch(() => {});
        }
      } catch { /* unsupported */ }
      try {
        if (!alarmRef.current) { const a = new Audio(alarmDataUri()); a.preload = 'auto'; alarmRef.current = a; }
        if (!alarmUnlockedRef.current) {
          const a = alarmRef.current;
          a.muted = true; a.volume = 0;
          const done = () => { try { a.pause(); a.currentTime = 0; a.muted = false; a.volume = 1; } catch { /* noop */ } };
          const p = a.play();
          if (p && p.then) p.then(done).catch(() => { a.muted = false; a.volume = 1; });
          else done();
          alarmUnlockedRef.current = true;
        }
      } catch { /* noop */ }
    };
    const opts = { once: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    return () => { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  }, []);

  return { dueQueue, acknowledge };
}
